import type {
  AdAgg,
  AppRules,
  CategoryId,
  FeeCheckRow,
  FeePreviewRanges,
  FeePreviewRow,
  FileIssue,
  ProductMapRow,
  ReportResult,
  SourceRow,
  StorageFeeRow,
  SummaryRow
} from "./types";
import {
  addDays,
  daysBetween,
  monthKey,
  monthLabel,
  round,
  safeDivide,
  sum,
  uniqueSortedDates,
  weekInterval
} from "./utils";
import {
  parseAllOrders,
  parseCustomerReturns,
  parseFeePreview,
  parseProductMap,
  parseSbAds,
  parseSdAds,
  parseSpAds,
  parseStorageFees,
  parseTransactions
} from "./parser";

export type FileBuckets = Record<CategoryId, File[]>;

const categoryNames: Record<CategoryId, string> = {
  productMap: "对应表",
  allOrder: "ALL ORDER",
  feePreview: "FEE PREVIEW",
  spAds: "SP 广告",
  sbAds: "SB 广告",
  sdAds: "SD 广告",
  storageFee: "STORAGE FEE",
  transaction: "TRANSACTION",
  customerReturn: "CUSTOMER RETURN"
};

export async function buildReport(
  files: FileBuckets,
  rules: AppRules,
  feePreviewRanges: FeePreviewRanges = {}
): Promise<ReportResult> {
  const issues: FileIssue[] = [];
  validateRequiredUploads(files, issues);

  const productResult = await parseProductMap(files.productMap);
  issues.push(...productResult.issues);
  const products = dedupeProducts(productResult.products, issues);

  const skuToProduct = new Map<string, ProductMapRow>();
  const asinToProduct = new Map<string, ProductMapRow>();
  for (const product of products) {
    skuToProduct.set(product.sku, product);
    if (!asinToProduct.has(product.asin)) asinToProduct.set(product.asin, product);
  }

  const [
    allOrderResult,
    feePreviewResult,
    spResult,
    sdResult,
    storageResult,
    transactionResult,
    customerReturnResult
  ] = await Promise.all([
    parseAllOrders(files.allOrder, rules),
    parseFeePreview(files.feePreview, feePreviewRanges),
    parseSpAds(files.spAds),
    parseSdAds(files.sdAds),
    parseStorageFees(files.storageFee),
    parseTransactions(files.transaction, skuToProduct),
    parseCustomerReturns(files.customerReturn, skuToProduct, asinToProduct)
  ]);
  issues.push(
    ...allOrderResult.issues,
    ...feePreviewResult.issues,
    ...spResult.issues,
    ...sdResult.issues,
    ...storageResult.issues,
    ...transactionResult.issues,
    ...customerReturnResult.issues
  );

  const sbResult = await parseSbAds(files.sbAds, rules, skuToProduct);
  issues.push(...sbResult.issues);

  const ads = combineAds(spResult.ads, sbResult.ads, sdResult.ads);
  const storageLookup = buildStorageLookup(storageResult.storageFees);
  const feePreviewLookup = buildFeePreviewLookup(feePreviewResult.fees);

  const dateList = uniqueSortedDates([
    ...allOrderResult.dates,
    ...spResult.dates,
    ...sbResult.dates,
    ...sdResult.dates,
    ...transactionResult.dates,
    ...customerReturnResult.dates
  ]);
  const earliestDate = dateList[0] ?? "";
  const latestDate = dateList[dateList.length - 1] ?? "";

  const sourceRows: SourceRow[] = [];
  const feeChecks: FeeCheckRow[] = [];
  const storageMissingKeys = new Set<string>();
  const feePreviewOverlapKeys = new Set<string>();

  if (earliestDate && latestDate) {
    const span = daysBetween(earliestDate, latestDate);
    for (let offset = 0; offset <= span; offset += 1) {
      const date = addDays(earliestDate, offset);
      for (const product of products) {
        const key = `${date}|${product.asin}|${product.sku}`;
        const asinKey = `${date}|${product.asin}`;
        const order = allOrderResult.orders.get(key);
        const transaction = transactionResult.transactions.get(key);
        const ad = ads.get(asinKey);
        const customerReturn = customerReturnResult.returns.get(key);
        const storage = resolveStorageFee(
          product.asin,
          date,
          storageLookup,
          rules,
          issues,
          storageMissingKeys
        );
        const feePreview = resolveFeePreview(
          product,
          date,
          feePreviewLookup,
          issues,
          feePreviewOverlapKeys
        );

        const sales = order?.sales ?? 0;
        const quantity = order?.quantity ?? 0;
        const actualDeliveryFee = transaction?.actualFbaFee ?? 0;
        const estimatedDeliveryFee = quantity * (feePreview?.feePerUnit ?? 0);
        const deliveryFee = estimatedDeliveryFee;
        const deliveryFeeSource = quantity
          ? feePreview
            ? `Fee Preview：${feePreview.fileName}${rangeLabel(feePreview)}`
            : "缺Fee Preview配送费"
          : "";

        const actualCommission = transaction?.actualSellingFee ?? 0;
        const estimatedCommission = actualCommission ? 0 : sales * rules.commissionRate;
        const commission = actualCommission || estimatedCommission;
        const commissionSource = actualCommission
          ? "Transaction实收"
          : sales
            ? "佣金率估算"
            : "";

        const landingCost = quantity * product.landingCostUsd;
        const storageFee = storage.dailyFee;
        const adjustment = transaction?.adjustment ?? 0;
        const refundAmount = transaction?.refundAmount ?? 0;
        const returnLandingCost = customerReturn?.returnLandingCost ?? 0;
        const profit =
          sales -
          deliveryFee -
          (ad?.spend ?? 0) -
          storageFee +
          adjustment +
          refundAmount +
          returnLandingCost -
          landingCost -
          commission;

        const flags: string[] = [];
        if (quantity && !estimatedDeliveryFee) flags.push("缺配送费");
        if (quantity && !actualCommission && !estimatedCommission) flags.push("缺佣金");
        if (storage.missing) flags.push("缺库存费");
        if (quantity && !feePreview) flags.push("缺Fee Preview");

        const row: SourceRow = {
          channle: product.channel,
          "周区间": weekInterval(date),
          "日期": date,
          "listing name": product.listingName,
          asin: product.asin,
          sku: product.sku,
          "总实际销售额": round(sales),
          "总销量": round(quantity),
          "总配送费": round(deliveryFee),
          "曝光": round(ad?.impressions ?? 0),
          "点击": round(ad?.clicks ?? 0),
          "花费": round(ad?.spend ?? 0),
          "总广告单量": round(ad?.adOrders ?? 0),
          "总广告销量": round(ad?.adUnits ?? 0),
          "广告sku销量": round(ad?.adSkuUnits ?? 0),
          "广告其他sku销量": round(ad?.adOtherSkuUnits ?? 0),
          "广告sku销售额": round(ad?.adSkuSales ?? 0),
          "总广告销售额": round(ad?.adSales ?? 0),
          "广告其他sku销售额": round(ad?.adOtherSkuSales ?? 0),
          "仓储费/天": round(storageFee),
          "调整金额": round(adjustment),
          "退款数量": round(transaction?.refundQuantity ?? 0),
          "退款金额": round(refundAmount),
          "COSTOMER RETURN.总落地成本": round(returnLandingCost),
          "总落地成本": round(landingCost),
          "实收配送费": round(actualDeliveryFee),
          "估算配送费": round(estimatedDeliveryFee),
          "配送费来源": deliveryFeeSource,
          "实收佣金": round(actualCommission),
          "估算佣金": round(estimatedCommission),
          "佣金来源": commissionSource,
          "佣金": round(commission),
          "库存费来源月份": storage.sourceMonth,
          "利润": round(profit),
          "利润率": round(safeDivide(profit, sales)),
          "异常标记": flags.join("；")
        };
        sourceRows.push(row);

        if (quantity || actualDeliveryFee || estimatedDeliveryFee || actualCommission || estimatedCommission) {
          feeChecks.push({
            key,
            asin: product.asin,
            sku: product.sku,
            date,
            quantity,
            actualFbaFee: round(actualDeliveryFee),
            estimatedFbaFee: round(estimatedDeliveryFee),
            actualSellingFee: round(actualCommission),
            estimatedSellingFee: round(estimatedCommission),
            deliveryFeeSource,
            commissionSource
          });
        }
      }
    }
  }

  const weeklyRows = summarize(sourceRows, (row) => row["周区间"]);
  const monthlyRows = summarize(sourceRows, (row) => monthLabel(row["日期"]));

  return {
    sourceRows,
    weeklyRows,
    monthlyRows,
    issues,
    feeChecks,
    stats: {
      productCount: products.length,
      sourceRowCount: sourceRows.length,
      orderCount: allOrderResult.orderCount,
      transactionFileCount: transactionResult.fileCount,
      transactionRowCount: transactionResult.rowCount,
      earliestDate,
      latestDate
    }
  };
}

function validateRequiredUploads(files: FileBuckets, issues: FileIssue[]): void {
  for (const category of ["productMap", "allOrder"] as CategoryId[]) {
    if (!files[category]?.length) {
      issues.push({
        severity: "error",
        category,
        message: `${categoryNames[category]} 是必需上传项。`
      });
    }
  }
}

function dedupeProducts(products: ProductMapRow[], issues: FileIssue[]): ProductMapRow[] {
  const seen = new Map<string, ProductMapRow>();
  for (const product of products) {
    const key = `${product.asin}|${product.sku}`;
    if (seen.has(key)) {
      issues.push({
        severity: "warning",
        category: "productMap",
        message: "对应表存在重复 ASIN/SKU，已保留第一条。",
        key
      });
      continue;
    }
    seen.set(key, product);
  }
  return Array.from(seen.values());
}

function combineAds(...sources: Array<Map<string, AdAgg>>): Map<string, AdAgg> {
  const result = new Map<string, AdAgg>();
  for (const source of sources) {
    for (const [key, row] of source.entries()) {
      const existing = result.get(key);
      if (!existing) {
        result.set(key, { ...row });
        continue;
      }
      existing.impressions += row.impressions;
      existing.clicks += row.clicks;
      existing.spend += row.spend;
      existing.adOrders += row.adOrders;
      existing.adUnits += row.adUnits;
      existing.adSkuUnits += row.adSkuUnits;
      existing.adOtherSkuUnits += row.adOtherSkuUnits;
      existing.adSkuSales += row.adSkuSales;
      existing.adOtherSkuSales += row.adOtherSkuSales;
      existing.adSales += row.adSales;
    }
  }
  return result;
}

interface FeePreviewLookup {
  bySku: Map<string, FeePreviewRow[]>;
  byAsin: Map<string, FeePreviewRow[]>;
}

function buildFeePreviewLookup(rows: FeePreviewRow[]): FeePreviewLookup {
  const bySku = new Map<string, FeePreviewRow[]>();
  const byAsin = new Map<string, FeePreviewRow[]>();
  for (const row of rows) {
    addListItem(byAsin, row.asin, row);
    if (row.sku) addListItem(bySku, row.sku, row);
  }
  return { bySku, byAsin };
}

function addListItem<T>(map: Map<string, T[]>, key: string, item: T): void {
  const list = map.get(key) ?? [];
  list.push(item);
  map.set(key, list);
}

function resolveFeePreview(
  product: ProductMapRow,
  date: string,
  lookup: FeePreviewLookup,
  issues: FileIssue[],
  overlapKeys: Set<string>
): FeePreviewRow | undefined {
  const skuMatches = filterFeePreviewByDate(lookup.bySku.get(product.sku) ?? [], date);
  const asinMatches = skuMatches.length ? [] : filterFeePreviewByDate(lookup.byAsin.get(product.asin) ?? [], date);
  const matches = skuMatches.length ? skuMatches : asinMatches;
  if (!matches.length) return undefined;
  const sorted = [...matches].sort((a, b) => a.fileIndex - b.fileIndex);
  if (sorted.length > 1) {
    const issueKey = `${product.asin}|${product.sku}|${sorted.map((item) => item.fileKey).join("/")}`;
    if (!overlapKeys.has(issueKey)) {
      overlapKeys.add(issueKey);
      issues.push({
        severity: "warning",
        category: "feePreview",
        message: "多个 Fee Preview 文件的适用日期范围同时匹配，已使用上传顺序靠后的文件。",
        key: `${date}|${product.asin}|${product.sku}|${sorted.map((item) => item.fileName).join(" / ")}`
      });
    }
  }
  return sorted[sorted.length - 1];
}

function filterFeePreviewByDate(rows: FeePreviewRow[], date: string): FeePreviewRow[] {
  return rows.filter((row) => (!row.startDate || date >= row.startDate) && (!row.endDate || date <= row.endDate));
}

function rangeLabel(row: FeePreviewRow): string {
  if (!row.startDate && !row.endDate) return "（全时段）";
  return `（${row.startDate || "最早"}~${row.endDate || "最新"}）`;
}

interface StorageLookup {
  byAsin: Map<string, StorageFeeRow[]>;
}

function buildStorageLookup(rows: StorageFeeRow[]): StorageLookup {
  const byAsin = new Map<string, StorageFeeRow[]>();
  for (const row of rows) {
    const list = byAsin.get(row.asin) ?? [];
    list.push(row);
    byAsin.set(row.asin, list);
  }
  for (const list of byAsin.values()) {
    list.sort((a, b) => a.month.localeCompare(b.month));
  }
  return { byAsin };
}

function resolveStorageFee(
  asin: string,
  date: string,
  lookup: StorageLookup,
  rules: AppRules,
  issues: FileIssue[],
  missingKeys: Set<string>
): { dailyFee: number; sourceMonth: string; missing: boolean } {
  const targetMonth = monthKey(date);
  const rows = lookup.byAsin.get(asin) ?? [];
  let row = rows.find((item) => item.month === targetMonth);
  if (!row) {
    row = rows
      .filter((item) => item.month <= targetMonth)
      .sort((a, b) => b.month.localeCompare(a.month))[0];
  }
  if (!row) {
    const issueKey = `${asin}|${targetMonth}`;
    if (rows.length === 0 && !missingKeys.has(issueKey)) {
      missingKeys.add(issueKey);
      issues.push({
        severity: "warning",
        category: "storageFee",
        message: "ASIN 没有可用库存费，已按 0 处理。",
        key: issueKey
      });
    }
    return { dailyFee: 0, sourceMonth: "", missing: true };
  }
  const monthNumber = Number.parseInt(targetMonth.slice(5, 7), 10);
  const multiplier = rules.peakMonths.includes(monthNumber) ? rules.peakMultiplier : 1;
  return {
    dailyFee: row.dailyFee * multiplier,
    sourceMonth: row.month + (multiplier !== 1 ? ` x${multiplier}` : ""),
    missing: false
  };
}

function summarize(rows: SourceRow[], labelGetter: (row: SourceRow) => string): SummaryRow[] {
  const grouped = new Map<string, SourceRow[]>();
  for (const row of rows) {
    const label = labelGetter(row);
    const list = grouped.get(label) ?? [];
    list.push(row);
    grouped.set(label, list);
  }
  const body = Array.from(grouped.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, group]) => summaryRow(label, group));
  if (body.length) body.push(summaryRow("总计", rows));
  return body;
}

function summaryRow(label: string, rows: SourceRow[]): SummaryRow {
  const sales = sum(rows, (row) => row["总实际销售额"]);
  const quantity = sum(rows, (row) => row["总销量"]);
  const profit = sum(rows, (row) => row["利润"]);
  const adSpend = sum(rows, (row) => row["花费"]);
  const deliveryFee = sum(rows, (row) => row["总配送费"]);
  const storageFee = sum(rows, (row) => row["仓储费/天"]);
  const refundAmount = sum(rows, (row) => row["退款金额"]);
  const landingCost = sum(rows, (row) => row["总落地成本"]);
  const commission = sum(rows, (row) => row["佣金"]);
  return {
    "行标签": label,
    "销售额": round(sales),
    "销量": round(quantity),
    "单价": round(safeDivide(sales, quantity)),
    "利润": round(profit),
    "利润率": round(safeDivide(profit, sales)),
    "ACOAS": round(safeDivide(adSpend, sales)),
    "配送费占比": round(safeDivide(deliveryFee, sales)),
    "仓储费占比": round(safeDivide(storageFee, sales)),
    "退货占比": round(safeDivide(-refundAmount, sales)),
    "落地成本占比": round(safeDivide(landingCost, sales)),
    "落地成本": round(landingCost),
    "配送费": round(deliveryFee),
    "广告花费": round(adSpend),
    "仓储费": round(storageFee),
    "退款金额": round(refundAmount),
    "佣金": round(commission)
  };
}
